/**
 * Demo overlay — a fixed-position banner with role-switch + reset controls.
 *
 * Vanilla DOM on purpose:
 *   - Keeps the bootstrap bundle small (no React).
 *   - Lives in its own DOM subtree so uiv2's React tree doesn't see it.
 *   - Means uiv2 needs zero awareness of the overlay's existence.
 *
 * If this ever grows past a few buttons, switch to React-in-its-own-root
 * (separate `ReactDOM.createRoot` so we still don't share React with uiv2).
 */

import { getState, resetState, updateState } from "@/mocks/state";

const OVERLAY_ID = "rasse-demo-overlay";

function styled(el: HTMLElement, styles: Partial<CSSStyleDeclaration>) {
  Object.assign(el.style, styles);
  return el;
}

function makeButton(label: string, onClick: () => void, danger = false) {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = label;
  styled(b, {
    padding: "4px 10px",
    borderRadius: "4px",
    border: danger
      ? "1px solid rgba(248, 113, 113, 0.4)"
      : "1px solid rgba(255, 255, 255, 0.15)",
    background: danger
      ? "rgba(248, 113, 113, 0.1)"
      : "rgba(255, 255, 255, 0.04)",
    color: danger ? "#fca5a5" : "#e5e5e5",
    cursor: "pointer",
    fontSize: "11px",
    fontWeight: "500",
    fontFamily: "inherit",
  });
  b.addEventListener("click", onClick);
  return b;
}

function buildBar(): HTMLElement {
  const bar = document.createElement("div");
  bar.id = OVERLAY_ID;
  bar.setAttribute("role", "region");
  bar.setAttribute("aria-label", "Demo controls");
  styled(bar, {
    position: "fixed",
    top: "0",
    left: "0",
    right: "0",
    // One below the int32 max so a real modal can still sit above the overlay.
    zIndex: "2147483646",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 16px",
    background: "rgba(10, 10, 10, 0.95)",
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
    color: "#e5e5e5",
    fontFamily:
      'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: "12px",
    boxShadow: "0 1px 8px rgba(0, 0, 0, 0.35)",
    pointerEvents: "auto",
  });

  const label = document.createElement("span");
  label.textContent = "Demo Mode";
  styled(label, {
    fontWeight: "600",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: "#a3a3a3",
    fontSize: "10px",
  });
  bar.appendChild(label);

  const controls = document.createElement("div");
  styled(controls, { display: "flex", gap: "8px" });

  // Role-switch button, only relevant once a user is "logged in".
  const renderControls = () => {
    controls.innerHTML = "";
    const state = getState();
    if (state.isAuthenticated) {
      const switchLabel =
        state.currentRole === "admin" ? "Switch to User" : "Switch to Admin";
      controls.appendChild(
        makeButton(switchLabel, () => {
          const newRole = state.currentRole === "admin" ? "user" : "admin";
          updateState({ currentRole: newRole });
          if (state.currentCallsign) {
            const user = state.users.find(
              (u) => u.callsign === state.currentCallsign,
            );
            if (user) {
              if (newRole === "admin" && !user.roles.includes("admin")) {
                user.roles.push("admin");
              } else if (newRole === "user") {
                user.roles = user.roles.filter((r) => r !== "admin");
              }
              updateState({ users: state.users });
            }
          }
          window.location.reload();
        }),
      );
    }
    controls.appendChild(
      makeButton(
        "Reset Demo",
        () => {
          resetState();
          window.location.href = "/login";
        },
        /* danger */ true,
      ),
    );
  };

  renderControls();
  // Re-render when the tab regains focus — covers manual sessionStorage
  // edits or state changes from a different tab.
  window.addEventListener("focus", renderControls);

  bar.appendChild(controls);
  return bar;
}

let mounted = false;

export function mountDemoOverlay() {
  if (mounted) return;
  mounted = true;
  if (document.getElementById(OVERLAY_ID)) return;
  const bar = buildBar();
  document.body.appendChild(bar);
  // Push body content down so the fixed bar doesn't overlap page content.
  const applyPadding = () => {
    document.body.style.paddingTop = `${bar.offsetHeight}px`;
  };
  applyPadding();
  new ResizeObserver(applyPadding).observe(bar);
}
